/**
 * Coach Check-Ins — Schedule automated check-ins per client.
 * Uses AsyncStorage for config; Supabase for check-in responses.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';

/* ── Types ── */
export interface CheckInQuestion {
  id: string;
  label: string;
  type: 'rating' | 'text' | 'yesno';
}

export const DEFAULT_QUESTIONS: CheckInQuestion[] = [
  { id: 'session_rating', label: 'Rate your last session (1-5)', type: 'rating' },
  { id: 'pain', label: 'Any pain or discomfort?', type: 'yesno' },
  { id: 'energy', label: 'Energy level (1-5)', type: 'rating' },
  { id: 'notes', label: 'Anything else to share?', type: 'text' },
];

export interface CheckInConfig {
  clientId: string;
  enabled: boolean;
  /** 0=Sun, 1=Mon ... 6=Sat */
  days: number[];
  questions: CheckInQuestion[];
}

export interface CheckInResponse {
  id: string;
  coach_id: string;
  client_id: string;
  answers: Record<string, string | number | boolean>;
  submitted_at: string;
}

/* ── Storage helpers ── */
const CONFIG_KEY = (coachId: string) => `checkin_configs_${coachId}`;

export async function loadCheckInConfigs(coachId: string): Promise<CheckInConfig[]> {
  try {
    const raw = await AsyncStorage.getItem(CONFIG_KEY(coachId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export async function saveCheckInConfig(coachId: string, config: CheckInConfig): Promise<void> {
  const all = await loadCheckInConfigs(coachId);
  const idx = all.findIndex((c) => c.clientId === config.clientId);
  if (idx >= 0) all[idx] = config; else all.push(config);
  await AsyncStorage.setItem(CONFIG_KEY(coachId), JSON.stringify(all));
}

export async function getCheckInConfig(coachId: string, clientId: string): Promise<CheckInConfig | null> {
  const all = await loadCheckInConfigs(coachId);
  return all.find((c) => c.clientId === clientId) ?? null;
}

/* ── Response storage (Supabase) ── */
export async function submitCheckIn(
  coachId: string,
  clientId: string,
  answers: Record<string, string | number | boolean>,
): Promise<void> {
  const response: Omit<CheckInResponse, 'id'> & { id: string } = {
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    coach_id: coachId,
    client_id: clientId,
    answers,
    submitted_at: new Date().toISOString(),
  };

  const { error } = await supabase.from('checkin_responses').insert(response);
  if (error) console.warn('[CheckIn] submit error', error.message);
}

export async function loadCheckInResponses(
  coachId: string,
  clientId: string,
  limit = 20,
): Promise<CheckInResponse[]> {
  const { data, error } = await supabase
    .from('checkin_responses')
    .select('*')
    .eq('coach_id', coachId)
    .eq('client_id', clientId)
    .order('submitted_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[CheckIn] load responses error', error.message);
    return [];
  }
  return (data ?? []) as CheckInResponse[];
}

/* ── Check if client is due for check-in today ── */
export function isDueToday(config: CheckInConfig): boolean {
  if (!config.enabled) return false;
  const today = new Date().getDay(); // 0=Sun
  return config.days.includes(today);
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export function formatDays(days: number[]): string {
  return days.map((d) => DAY_LABELS[d]).join(', ');
}
